import { CardElement, PlayStyle } from "../types/battle"
import {
  CardShopCatalog,
  CardShopOffer,
  CardShopPoint,
  CardShopPurchaseResponse,
} from "../types/cardShop"
import { normalizeGameCard } from "./gameCards"

type RawRecord = Record<string, unknown>

const CARD_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const PLAY_STYLES: PlayStyle[] = ["ATTACK", "DEFENSE", "SPEED"]

const DEFAULT_RADIUS_M = 50
const DEFAULT_DAILY_LIMIT = 3

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  const normalized = pickString(value).toUpperCase()
  return allowed.includes(normalized as T) ? (normalized as T) : fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

function normalizePointCore(raw: RawRecord): CardShopPoint | null {
  const id = raw.id ?? raw.uuid ?? raw.pointId ?? raw.point_id
  const lat = pickNumber(raw.lat ?? raw.latitude)
  const lng = pickNumber(raw.lng ?? raw.longitude ?? raw.lon)
  if (id == null || lat == null || lng == null) return null

  const dailyLimit =
    pickNumber(raw.dailyLimit ?? raw.daily_limit, DEFAULT_DAILY_LIMIT) ??
    DEFAULT_DAILY_LIMIT
  const purchasesToday =
    pickNumber(raw.purchasesToday ?? raw.purchases_today, 0) ?? 0
  const purchasesRemaining =
    pickNumber(
      raw.purchasesRemaining ??
        raw.purchases_remaining ??
        Math.max(0, dailyLimit - purchasesToday),
      Math.max(0, dailyLimit - purchasesToday)
    ) ?? Math.max(0, dailyLimit - purchasesToday)

  return {
    id: String(id),
    name: pickString(raw.name ?? raw.title, `Лавка ${id}`),
    lat,
    lng,
    radiusM:
      pickNumber(
        raw.radiusM ?? raw.radiusMeters ?? raw.radius_meters ?? raw.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
    purchasesToday,
    purchasesRemaining,
    dailyLimit,
  }
}

export function normalizeCardShopPoint(raw: unknown): CardShopPoint | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  return normalizePointCore(raw as RawRecord)
}

export function mapCardShopPoints(data: unknown): CardShopPoint[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapCardShopPoints(JSON.parse(data))
    } catch {
      return []
    }
  }
  if (Array.isArray(data)) {
    return data
      .map(normalizeCardShopPoint)
      .filter((point): point is CardShopPoint => point != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.points ?? obj.items ?? obj.shops ?? obj.cardShops ?? obj.card_shops
  if (Array.isArray(list)) {
    return list
      .map(normalizeCardShopPoint)
      .filter((point): point is CardShopPoint => point != null)
  }
  const single = normalizeCardShopPoint(obj)
  return single ? [single] : []
}

function normalizeOffer(raw: unknown): CardShopOffer | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.offerId ?? item.offer_id
  if (id == null) return null
  return {
    id: String(id),
    element: pickEnum(item.element, CARD_ELEMENTS, "FIRE"),
    style: pickEnum(item.style, PLAY_STYLES, "ATTACK"),
    priceCoins: pickNumber(item.priceCoins ?? item.price_coins, 0) ?? 0,
  }
}

export function normalizeCardShopCatalog(data: unknown): CardShopCatalog | null {
  const obj = unwrapPayload(data)
  const pointRaw = (obj.point ?? obj.shop ?? obj.cardShop ?? obj) as RawRecord
  const pointCore = normalizePointCore(pointRaw)
  if (!pointCore) return null

  const offersRaw = obj.offers ?? obj.items ?? obj.catalog ?? []
  const offers = Array.isArray(offersRaw)
    ? offersRaw
        .map(normalizeOffer)
        .filter((offer): offer is CardShopOffer => offer != null)
    : []

  const dailyLimit =
    pickNumber(obj.dailyLimit ?? obj.daily_limit, pointCore.dailyLimit) ??
    pointCore.dailyLimit
  const purchasesToday =
    pickNumber(obj.purchasesToday ?? obj.purchases_today, pointCore.purchasesToday) ??
    pointCore.purchasesToday
  const purchasesRemaining =
    pickNumber(
      obj.purchasesRemaining ?? obj.purchases_remaining,
      pointCore.purchasesRemaining
    ) ?? pointCore.purchasesRemaining

  return {
    point: {
      id: pointCore.id,
      name: pointCore.name,
      lat: pointCore.lat,
      lng: pointCore.lng,
      radiusM: pointCore.radiusM,
    },
    inRange: pickBool(obj.inRange ?? obj.in_range, false),
    distanceMeters: pickNumber(obj.distanceMeters ?? obj.distance_meters, 0) ?? 0,
    purchasesToday,
    purchasesRemaining,
    dailyLimit,
    offers,
  }
}

export function normalizeCardShopPurchaseResponse(
  data: unknown
): CardShopPurchaseResponse | null {
  const obj = unwrapPayload(data)
  const card = normalizeGameCard(obj.card)
  if (!card) return null

  return {
    card,
    coins: pickNumber(obj.coins, 0) ?? 0,
    purchasesToday: pickNumber(obj.purchasesToday ?? obj.purchases_today, 0) ?? 0,
    purchasesRemaining:
      pickNumber(obj.purchasesRemaining ?? obj.purchases_remaining, 0) ?? 0,
  }
}

export const CARD_SHOP_ELEMENTS: CardElement[] = [...CARD_ELEMENTS]
export const CARD_SHOP_STYLES: PlayStyle[] = [...PLAY_STYLES]

export function sortCardShopOffers(offers: CardShopOffer[]): CardShopOffer[] {
  return [...offers].sort((a, b) => {
    const elementDiff =
      CARD_ELEMENTS.indexOf(a.element) - CARD_ELEMENTS.indexOf(b.element)
    if (elementDiff !== 0) return elementDiff
    return PLAY_STYLES.indexOf(a.style) - PLAY_STYLES.indexOf(b.style)
  })
}
